import React, { useMemo } from "react";
import Icon from "../../../components/AppIcon";
import { getAprStatusMeta } from "../aprWorkflowStats";

const STATUS_SORT = { none: 0, Start: 1, Pending: 2, Completed: 3, Closed: 4 };

const fieldClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-[#16325a]/40 focus:outline-none focus:ring-2 focus:ring-[#16325a]/15";

const DistrictSubmissionTable = ({
  rows,
  loading,
  reportingYear,
  regionFilter,
  onRegionFilterChange,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  regionOptions,
  statusOptions,
}) => {
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ra = a.regionName.localeCompare(b.regionName);
      if (ra !== 0) return ra;
      const sa =
        (STATUS_SORT[getAprStatusMeta(a.status).key] ?? 0) -
        (STATUS_SORT[getAprStatusMeta(b.status).key] ?? 0);
      if (sa !== 0) return sa;
      return a.districtName.localeCompare(b.districtName);
    });
  }, [rows]);

  const submittedCount = rows.filter((r) => getAprStatusMeta(r.status).submittedToRcc).length;

  return (
    <section className="overflow-hidden rounded-2xl border border-[#16325a]/12 bg-white shadow-[0_8px_24px_rgba(22,50,90,0.05)]">
      <div className="border-b border-slate-100 bg-gradient-to-br from-[#16325a]/[0.04] via-white to-teal-50/40 px-4 py-5 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-1 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#16325a]/70">
              <Icon name="ListChecks" size={14} color="#16325a" />
              District ledger
            </div>
            <h3 className="text-lg font-semibold text-[#16325a] md:text-xl">
              District submission status
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {reportingYear} under review —{" "}
              <span className="font-medium text-slate-700">{rows.length}</span> district(s)
              {rows.length > 0 && (
                <>
                  {" "}
                  ·{" "}
                  <span className="font-medium text-teal-700">{submittedCount}</span> submitted to
                  RCC or beyond
                </>
              )}
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-3xl lg:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Region
              </label>
              <select
                className={fieldClass}
                value={regionFilter}
                onChange={(e) => onRegionFilterChange(e.target.value)}
              >
                {regionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Status
              </label>
              <select
                className={fieldClass}
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Search
              </label>
              <div className="relative">
                <Icon
                  name="Search"
                  size={16}
                  color="#64748b"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                />
                <input
                  type="search"
                  placeholder="District or region…"
                  className={`${fieldClass} pl-9`}
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="sticky top-0 z-[1]">
            <tr className="border-b border-slate-100 bg-slate-50/90 text-[11px] uppercase tracking-[0.08em] text-slate-500 backdrop-blur">
              <th className="px-4 py-3.5 font-semibold md:px-6">Region</th>
              <th className="px-4 py-3.5 font-semibold md:px-6">District</th>
              <th className="px-4 py-3.5 font-semibold md:px-6">Report status</th>
              <th className="px-4 py-3.5 font-semibold md:px-6">Submitted to RCC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-16 text-center md:px-6">
                  <div className="inline-flex flex-col items-center gap-3 text-slate-500">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#16325a] border-t-transparent" />
                    <span>Loading district statuses…</span>
                  </div>
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-16 text-center md:px-6">
                  <div className="inline-flex flex-col items-center gap-2 text-slate-500">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
                      <Icon name="Inbox" size={22} color="#64748b" />
                    </div>
                    <p className="font-medium text-slate-700">No districts match</p>
                    <p className="text-xs">Try clearing filters or changing the year.</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const meta = getAprStatusMeta(row.status);
                return (
                  <tr
                    key={row.districtId}
                    className="transition-colors hover:bg-[#16325a]/[0.03]"
                  >
                    <td className="px-4 py-3.5 text-slate-500 md:px-6">{row.regionName}</td>
                    <td className="px-4 py-3.5 font-medium text-slate-800 md:px-6">
                      {row.districtName}
                    </td>
                    <td className="px-4 py-3.5 md:px-6">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${meta.badgeClass}`}
                      >
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 md:px-6">
                      {meta.submittedToRcc ? (
                        <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DistrictSubmissionTable;
